import { Component, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Role } from '@cms_root/app/roles/models/role';

interface NavItem {
  titleKey: string;
  path?: string;
  children?: NavItem[];
  isExpanded?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  visibleForRoles?: Role[];
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false
})
export class SidenavComponent {
  @Output() toggle = new EventEmitter<void>();

  readonly navItems: NavItem[];

  constructor(private readonly router: Router) {
    this.navItems = this.getNavItems();
  }

  selectItem(navItem: NavItem): void {
    if (navItem.disabled) {
      return;
    }

    if (navItem.children) {
      this.toggleSection(navItem);
      this.markNavItemAsActive(navItem.children[0]);
    } else {
      this.markNavItemAsActive(navItem);
    }
  }

  isAnyChildRouteActive(navItem: NavItem): boolean {
    return navItem.children?.some((child) => {
      if (child.disabled) {
        return undefined;
      }
      return this.router.isActive(child.path, false);
    });
  }

  private toggleSection(navItem: NavItem): void {
    if (navItem.isExpanded) {
      return;
    }

    this.navItems.forEach((navItem) => {
      navItem.isExpanded = false;
    });

    navItem.isExpanded = !navItem.isExpanded;
  }

  private markNavItemAsActive(navItem: NavItem): void {
    if (navItem.isActive) {
      return;
    }

    this.navItems.forEach((navItem) => {
      navItem.isActive = false;

      navItem?.children?.forEach((navSubitem) => (navSubitem.isActive = false));
    });

    navItem.isActive = true;
    this.router.navigate([navItem.path || '/']);
  }

  private getNavItems(): NavItem[] {
    return [
      {
        titleKey: 'sidenavcontent',
        visibleForRoles: [Role.admin, Role.reviewer, Role.pageContentPublisher],
        children: [
          {
            titleKey: 'sidenavpages',
            path: 'pages'
          }
        ]
      },
      {
        titleKey: 'sidenavconfiguration',
        visibleForRoles: [Role.admin],
        children: [
          {
            titleKey: 'sidenavpageswebspaces',
            path: 'pages-webspaces'
          },
          {
            titleKey: 'sidenavassetswebspaces',
            path: 'assets-webspaces'
          },
          {
            titleKey: 'sidenavlanguages',
            path: 'languages'
          },
          {
            titleKey: 'sidenavorigins',
            path: 'origins'
          },
          {
            titleKey: 'sidenavtaxonomies',
            path: 'taxonomies'
          },
          {
            titleKey: 'sidenavmicrosites',
            path: 'microsites'
          },
          {
            titleKey: 'sidenavsitenavigation',
            path: 'site-navigation'
          },
          {
            titleKey: 'sidenavsitesettings',
            path: 'site-settings'
          },
          {
            titleKey: 'sidenavtemplates',
            disabled: true
          }
        ]
      },
      {
        titleKey: 'sidenavreports',
        visibleForRoles: [Role.admin],
        children: [
          {
            titleKey: 'sidenavreports404',
            path: 'reports/404-report'
          },
          {
            titleKey: 'sidenavreportsurlhits',
            path: 'reports/url-hits-report'
          },
          {
            titleKey: 'sidenavreportsinventoryreport',
            path: 'reports/inventory-report'
          },
          {
            titleKey: 'sidenavreportsuseractivityreport',
            path: 'reports/user-activity-report'
          },
          {
            titleKey: 'sidenavreportshistoricaldataexport',
            path: 'reports/historical-data-export'
          },
          {
            titleKey: 'sidenavreportsrelatedpages',
            path: 'reports/related-pages-report'
          },
          {
            titleKey: 'sidenavreportscollections',
            path: 'reports/collections-report'
          }
        ]
      },
      {
        titleKey: 'sidenavassetsmanagement',
        visibleForRoles: [Role.admin, Role.reviewer, Role.assetContentPublisher],
        children: [
          {
            titleKey: 'sidenavassets',
            path: 'assets'
          }
        ]
      }
    ];
  }
}
